import {
  Button,
  darken,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import {
  getDisplayStatus,
  LOAD_STATUS,
} from 'doc-mate-store/lib/constants/load';
import moment from 'moment';
import React, { useCallback } from 'react';
import { colors } from '../../../themes';

type Props = {
  open: boolean;
  isLoading: boolean;
  newStatus: LOAD_STATUS | null;
  isManifestWideChange?: boolean;
  forwardStatusesToFill: LOAD_STATUS[];
  reverseStatusesToClear: LOAD_STATUS[];
  timestamps: Record<string, string>;
  onTimestampChange: (stat: string, value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
};

export const TimestampConfirmModal: React.FC<Props> = ({
  open,
  isLoading,
  newStatus,
  isManifestWideChange = false,
  forwardStatusesToFill,
  reverseStatusesToClear,
  timestamps,
  onTimestampChange,
  onConfirm,
  onCancel,
}) => {
  const classes = useStyles();

  // Formats ISO string to "YYYY-MM-DDTHH:mm" for native datetime-local controls
  const formatForInput = (isoStr: string) => {
    if (!isoStr) return '';
    const m = moment(isoStr);
    return m.isValid() ? m.format('YYYY-MM-DDTHH:mm') : '';
  };

  const handleConfirmSubmit = useCallback(() => {
    for (const [st, val] of Object.entries(timestamps)) {
      if (val) {
        const m = moment(val);
        if (m.isValid()) {
          onTimestampChange(st, m.toISOString());
        }
      }
    }
    onConfirm();
  }, [timestamps, onTimestampChange, onConfirm]);

  if (!newStatus) return null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="xs"
      className={classes.dialog}
    >
      <DialogTitle disableTypography className={classes.header}>
        <span className="linked-text">Confirm Status Change</span>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.main}>
        <div className="dialog-text">
          Are you sure you want to change status to{' '}
          <b>{getDisplayStatus(newStatus)}</b>?
        </div>

        {isManifestWideChange && (
          <div className={classes.linkedNotice}>
            <b>Notice:</b> All orders and legs for this manifest will be updated
            to <b>{getDisplayStatus(newStatus)}</b>.
          </div>
        )}

        {reverseStatusesToClear.length > 0 && (
          <div className={classes.warningBox}>
            <b>Warning:</b> Reversing status will clear timestamps for:
            <ul>
              {reverseStatusesToClear.map(st => (
                <li key={st}>{getDisplayStatus(st)}</li>
              ))}
            </ul>
          </div>
        )}

        {forwardStatusesToFill.length > 0 && (
          <div className={classes.timestampForm}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Please confirm/provide timestamps:
            </div>
            {forwardStatusesToFill.map(st => (
              <TextField
                key={st}
                label={getDisplayStatus(st)}
                type="datetime-local"
                value={formatForInput(timestamps[st])}
                onChange={e => {
                  const val = e.target.value;
                  onTimestampChange(st, val ? moment(val).toISOString() : '');
                }}
                fullWidth
                margin="dense"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        className={classes.inlineDoneBtn}
                        title="Done editing timestamp"
                        onClick={() => {
                          // Commits/formats current input value
                          const currentVal = timestamps[st];
                          if (currentVal && moment(currentVal).isValid()) {
                            onTimestampChange(
                              st,
                              moment(currentVal).toISOString(),
                            );
                          }
                        }}
                      >
                        <CheckIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ))}
          </div>
        )}

        <div className={classes.actions}>
          <Button
            disabled={isLoading}
            className={`${classes.actionBtn} ${classes.cancelBtn}`}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            disabled={isLoading}
            className={`${classes.actionBtn} ${classes.saveBtn}`}
            onClick={handleConfirmSubmit}
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paperWidthXs': { maxWidth: 420 },
  },
  header: {
    fontSize: 18,
    color: '#4f4f4f',
    fontWeight: 700,
    textAlign: 'center',
    padding: theme.spacing(1.5),
    '& .linked-text': { textTransform: 'uppercase' },
  },
  main: {
    padding: theme.spacing(1, 2),
    '& .dialog-text': {
      textAlign: 'center',
      fontSize: 15,
      padding: theme.spacing(1, 0),
      color: '#4f4f4f',
    },
  },
  linkedNotice: {
    backgroundColor: '#e8f4fd',
    color: '#0d3c61',
    padding: theme.spacing(1.2),
    borderRadius: 4,
    margin: theme.spacing(1, 0),
    fontSize: 13,
    textAlign: 'center',
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    color: '#856404',
    padding: theme.spacing(1.5),
    borderRadius: 4,
    margin: theme.spacing(1, 0),
    fontSize: 13,
    '& ul': { margin: '4px 0 0 0', paddingLeft: 20 },
  },
  timestampForm: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  inlineDoneBtn: {
    backgroundColor: '#e0e0e0',
    color: '#424242',
    padding: 4,
    '&:hover': {
      backgroundColor: '#4884a5',
      color: '#ffffff',
    },
  },
  actions: {
    alignItems: 'center',
    borderTop: '1px solid #bdbdbd',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
  },
  actionBtn: {
    borderRadius: 5,
    fontSize: theme.typography.body1.fontSize,
    marginLeft: theme.spacing(2),
    padding: theme.spacing(0.8, 3),
  },
  cancelBtn: {
    backgroundColor: colors.danger[500],
    color: 'white',
    '&:hover': { backgroundColor: darken(colors.danger[500], 0.15) },
  },
  saveBtn: {
    backgroundColor: colors.textPrimary[400],
    color: 'white',
    '&:hover': { backgroundColor: darken(colors.textPrimary[400], 0.15) },
  },
}));

export default React.memo(TimestampConfirmModal);
